import React from "react";
import { useParams } from "react-router-dom";
import "./styles.css";

import { dimensionMeta, burnoutIIDefinitions } from "./constants";
import GradientBar from "./charts/GradientBar";
import CircularProgress from "./charts/CircularProgress";
import WphiReport from "./WphiReport";
import RadarChart from "./charts/RadarChart";

import { useIqolData } from "./hooks/useIqolData";
import { useIqolGroups } from "./hooks/useIqolGroups";
import { getTreemapColor } from "./utils/color";

const IqolReport = () => {
  const { assignmentId } = useParams();

  const { iqol, burnout, lifestyles, burnoutII, error } =
    useIqolData(assignmentId);

  const { groups, report } = useIqolGroups(iqol);

  // Color logic function for average percentages
  const getAverageColor = (value) => {
    if (value >= 0 && value <= 49) {
      return "#ff5b33"; // RED
    } else if (value >= 50 && value <= 79) {
      return "#cdac27ff"; // YELLOW
    } else if (value >= 80 && value <= 100) {
      return "#41cd31"; // GREEN
    }
    return "#000"; // fallback color
  };

  const handleExportPdf = () => {
    window.print();
  };

  const burnoutCauses = React.useMemo(() => {
    if (!burnout || !Array.isArray(burnout)) return [];
    return burnout
      .filter(
        (item) =>
          item.dimension === "Burnout" &&
          item.habito &&
          typeof item.valor === "number"
      )
      .map((item) => ({
        label: item.habito,
        value: item.valor,
      }));
  }, [burnout]);

  const maxValue = Math.max(...burnoutCauses.map((i) => i.value), 1);
  const burnoutCausesWithPct = burnoutCauses.map((item) => ({
    ...item,
    pct: Math.round((item.value / maxValue) * 100),
  }));

  const lifestylesCauses = React.useMemo(() => {
    if (!lifestyles || !Array.isArray(lifestyles)) return [];
    return lifestyles
      .filter(
        (item) =>
          item.dimension === "Burnout" &&
          item.habito &&
          typeof item.valor === "number"
      )
      .map((item) => ({
        label: item.habito,
        value: item.valor,
        pct: item.valor, // Use actual value instead of relative percentage
      }));
  }, [lifestyles]);

  const burnoutIIHabitos = React.useMemo(() => {
    if (!burnoutII || !Array.isArray(burnoutII)) return [];
    return burnoutII
      .filter(
        (item) =>
          item.dimension === "Burnout" &&
          item.habito &&
          typeof item.valor === "number"
      )
      .map((item) => ({
        nombre: item.habito,
        valor: item.valor,
      }));
  }, [burnoutII]);

  const burnoutAverage = React.useMemo(() => {
    if (!burnoutIIHabitos.length) return 0;
    
    const findHabito = (name) => {
      const habito = burnoutIIHabitos.find(h => h.nombre === name);
      return habito ? habito.valor : 0;
    };
    
    const productividad = findHabito("Productividad");
    const agotamientoEmocional = findHabito("Agotamiento emocional");
    const despersonalizacion = findHabito("Despersonalizacion");
    console.log({productividad, agotamientoEmocional, despersonalizacion});
    // Formula: AVG(100-Productividad, Agotamiento Emocional, Despersonalizacion)
    const average = ((100 - productividad) + agotamientoEmocional + despersonalizacion) / 3;
    
    return Math.round(average);
  }, [burnoutIIHabitos]);

  const indiceCalidadVida = React.useMemo(() => {
    if (!groups.length) return 0;

    const dimensionsToInclude = [
      "Tu ser",
      "Tu ambiente",
      "Tus relaciones",
      "Tus percepciones",
      "Tus contribuciones",
    ];
    const relevantGroups = groups.filter((g) =>
      dimensionsToInclude.includes(g.dimension)
    );
    const avg = Math.round(
      relevantGroups.reduce((sum, g) => sum + g.average, 0) /
        relevantGroups.length
    );

    return avg;
  }, [groups]);

  const radarData = React.useMemo(() => {
    if (!groups.length) return {};
    const data = {};
    groups.forEach(({ dimension, average }) => {
      data[dimension] = average;
    });
    return data;
  }, [groups]);

  if (!assignmentId) return <p>No assignment ID supplied.</p>;
  if (error) return <p>Error loading report: {error.message}</p>;
  if (!report) return <p>Loading IQoL results…</p>;

  return (
    <div className="iqol-report">
      <button className="export-pdf-button" onClick={handleExportPdf}>
        Imprimir
      </button>
      <h1>Avalúo de Salud Real de Impactivista</h1>
      <p>
        Herramienta para apoyarte en el proceso de transformar tu Salud Real.
      </p>
      <br />

      <WphiReport assignmentId={assignmentId} />
      <div className="top-chart-container">
        <h2>Índice de Salud Real</h2>
        <CircularProgress percent={indiceCalidadVida} />
      </div>

      <div className="radar-chart-container">
        <RadarChart data={radarData} size={200} />
      </div>
      <br />
      <h1 className="resultados-heading">Resultados por dimensión</h1>

      {groups.map(({ dimension, average, habitos }) => {
        const meta = dimensionMeta[dimension];
        if (!meta) return null;
        return (
          <section key={dimension} className="dimension">
            <header>
              <h3>
                {meta.title} <span style={{ color: getAverageColor(average) }}> {average}%</span> 
              </h3>
            </header>

            <p className="habitos-description">
              {meta?.description?.split("\n").map((text, idx) => (
                <React.Fragment key={idx}>
                  {text}
                  <br />
                </React.Fragment>
              ))}
            </p>

            <div className="habitos-list">
              {habitos.map((h) => (
                <div key={h.nombre} className="habito">
                  <label>{h.nombre}</label>
                  <p>
                    {meta?.habitos?.[h.nombre]?.split("\n").map((text, idx) => (
                      <React.Fragment key={idx}>
                        {text}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                  <div className="habitos-chart">
                    {h.nombre === "Valoración negativa" ? (
                      <GradientBar percent={h.valor} invert={true} />
                    ) : (
                      <GradientBar percent={h.valor} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {burnoutIIHabitos.length > 0 && (
        <section className="burnoutII-section">
          <header>
            <h3>
              Burnout <span style={{ color: getAverageColor(burnoutAverage) }}> {burnoutAverage}%</span> 
            </h3>
          </header>
          <div className="habitos-list">
            {burnoutIIHabitos.map((h) => (
              <div key={h.nombre} className="habito">
                <label>{h.nombre}</label>
                <p>
                  {burnoutIIDefinitions?.[h.nombre]
                    ?.split("\n")
                    .map((text, idx) => (
                      <React.Fragment key={idx}>
                        {text}
                        <br />
                      </React.Fragment>
                    ))}
                </p>
                <div className="habitos-chart">
                  {h.nombre === "Productividad" ? (
                    <GradientBar percent={h.valor} />
                    ) : (
                    <GradientBar percent={h.valor} invert={true} />
                    )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="burnout-treemap-grid-outer">
        <div className="burnout-treemap-grid-title">Causas del burnout</div>
        <div className="burnout-treemap-grid-bar" />
        <div className="burnout-treemap-bar-labels">
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>
        <div className="burnout-treemap-grid">
          {burnoutCausesWithPct.map((item, idx) => {
            let gridClass = "";
            if (/carga/i.test(item.label)) gridClass = "treemap-grid-carga";
            else if (/equidad/i.test(item.label))
              gridClass = "treemap-grid-equidad";
            else if (/control/i.test(item.label))
              gridClass = "treemap-grid-control";
            else if (/valores/i.test(item.label))
              gridClass = "treemap-grid-valores";
            else if (/comunidad/i.test(item.label))
              gridClass = "treemap-grid-comunidad";
            else if (/recompensa/i.test(item.label))
              gridClass = "treemap-grid-recompensa";
            return (
              <div
                key={item.label}
                className={`treemap-box ${gridClass}`}
                style={{ background: getTreemapColor(item.pct) }}
              >
                <span className="treemap-label">{item.label}</span>
                <span className="treemap-percent">{item.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="burnout-treemap-grid-outer">
        <div className="burnout-treemap-grid-title">
          Pilares de la Medicina del Estilo de Vida: Fortalezas y Oportunidades
        </div>
        <div className="burnout-treemap-grid-bar" />
        <div className="burnout-treemap-bar-labels">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
        <div className="burnout-treemap-grid">
          {lifestylesCauses.map((item, idx) => {
            let gridClass = "";
            if (/actividad/i.test(item.label)) gridClass = "treemap-grid-carga";
            else if (/nutrición/i.test(item.label))
              gridClass = "treemap-grid-valores";
            else if (/manejo/i.test(item.label))
              gridClass = "treemap-grid-control";
            else if (/psicología/i.test(item.label))
              gridClass = "treemap-grid-equidad";
            else if (/comportamientos/i.test(item.label))
              gridClass = "treemap-grid-comunidad";
            else if (/sueño/i.test(item.label))
              gridClass = "treemap-grid-recompensa";
            return (
              <div
                key={item.label}
                className={`treemap-box ${gridClass}`}
                style={{ background: getTreemapColor(item.pct, true) }}
              >
                <span className="treemap-label">{item.label}</span>
                <span className="treemap-percent">{item.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
export default IqolReport;
